import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  ButtonGroup,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AttachMoney,
  ShoppingCart,
  People,
  TrendingUp,
  Assessment,
  Inventory2,
  Download,
  Print,
  CalendarToday,
  FilterList,
  MoreVert
} from '@mui/icons-material';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';

// Import custom chart components
import KPICard from '../components/charts/KPICard';
import TrendChart from '../components/charts/TrendChart';
import FunnelChart from '../components/charts/FunnelChart';
import ComparisonChart from '../components/charts/ComparisonChart';

const COLORS = ['#003366', '#D4AF37', '#4A90E2', '#50C878', '#FF6B6B', '#FFA500', '#9B59B6', '#34495E'];

const Analytics = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date Range State
  const [dateRange, setDateRange] = useState('last3months');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Analytics Data State
  const [overviewData, setOverviewData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [commissionsData, setCommissionsData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);

  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Calculate date range
  const getDateRange = () => {
    const end = new Date();
    let start = new Date();

    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        return {
          start_date: customStartDate,
          end_date: customEndDate
        };
      }
      return null;
    }

    switch (dateRange) {
      case 'thisMonth':
        start = startOfMonth(new Date());
        break;
      case 'lastMonth':
        start = startOfMonth(subMonths(new Date(), 1));
        end.setDate(0); // Last day of previous month
        break;
      case 'last3months':
        start = subMonths(new Date(), 3);
        break;
      case 'last6months':
        start = subMonths(new Date(), 6);
        break;
      case 'thisYear':
        start = new Date(new Date().getFullYear(), 0, 1);
        break;
      case 'lastYear':
        start = new Date(new Date().getFullYear() - 1, 0, 1);
        end = new Date(new Date().getFullYear() - 1, 11, 31);
        break;
      default:
        start = subMonths(new Date(), 3);
    }

    return {
      start_date: format(start, 'yyyy-MM-dd'),
      end_date: format(end, 'yyyy-MM-dd')
    };
  };

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = getDateRange();
      if (!params) {
        setError('Please select valid date range');
        setLoading(false);
        return;
      }

      // Fetch all analytics endpoints in parallel
      const [overview, sales, leads, inventory, revenue, commissions, staff, trends] = await Promise.all([
        api.get('/api/analytics/overview', { params }),
        api.get('/api/analytics/sales', { params }),
        api.get('/api/analytics/leads', { params }),
        api.get('/api/analytics/inventory', { params }),
        api.get('/api/analytics/revenue', { params }),
        api.get('/api/analytics/commissions', { params }),
        api.get('/api/analytics/staff', { params }),
        api.get('/api/analytics/trends', { params })
      ]);

      setOverviewData(overview.data);
      setSalesData(sales.data);
      setLeadsData(leads.data);
      setInventoryData(inventory.data);
      setRevenueData(revenue.data);
      setCommissionsData(commissions.data);
      setStaffData(staff.data);
      setTrendsData(trends.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, customStartDate, customEndDate]);

  const handleExportCSV = () => {
    // Simple CSV export of current tab data
    let csvContent = '';
    let filename = 'analytics-export.csv';

    switch (currentTab) {
      case 0: // Overview
        csvContent = 'Metric,Value\n';
        if (overviewData?.kpis) {
          Object.entries(overviewData.kpis).forEach(([key, value]) => {
            csvContent += `${key},${value}\n`;
          });
        }
        filename = 'overview-export.csv';
        break;
      case 1: // Sales
        if (salesData?.sales_by_month) {
          csvContent = 'Month,Count,Value\n';
          salesData.sales_by_month.forEach(row => {
            csvContent += `${row.month_name},${row.count},${row.value}\n`;
          });
        }
        filename = 'sales-export.csv';
        break;
      // Add more cases for other tabs
      default:
        csvContent = 'Data not available for export';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    setExportMenuAnchor(null);
  };

  const handlePrint = () => {
    window.print();
    setExportMenuAnchor(null);
  };

  // Overview Tab Component
  const OverviewTab = () => {
    if (!overviewData) return <CircularProgress />;

    const { kpis } = overviewData;
    const { month_over_month } = trendsData || {};

    return (
      <Grid container spacing={3}>
        {/* KPI Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Revenue"
            value={kpis.total_revenue}
            format="currency"
            icon={<AttachMoney />}
            color="success.main"
            trend={month_over_month?.revenue?.growth > 0 ? 'up' : 'down'}
            trendValue={month_over_month?.revenue?.growth}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Sales"
            value={kpis.sales_count}
            format="number"
            icon={<ShoppingCart />}
            color="primary.main"
            subtitle={`${kpis.completed_sales} completed`}
            trend={month_over_month?.sales?.growth > 0 ? 'up' : 'down'}
            trendValue={month_over_month?.sales?.growth}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Lead Conversion"
            value={kpis.conversion_rate}
            format="percent"
            icon={<People />}
            color="info.main"
            subtitle={`${kpis.converted_leads} / ${kpis.total_leads} leads`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Items Sold"
            value={kpis.items_sold}
            format="number"
            icon={<Inventory2 />}
            color="warning.main"
            subtitle={`${kpis.active_sales} active sales`}
          />
        </Grid>

        {/* Revenue Trend */}
        <Grid item xs={12} md={8}>
          <TrendChart
            title="Revenue Trend"
            data={revenueData?.monthly_revenue || []}
            dataKeys={[
              { dataKey: 'actual', name: 'Actual Revenue' },
              { dataKey: 'target', name: 'Target' }
            ]}
            xAxisKey="month_name"
            height={350}
            type="area"
            yAxisFormat="currency"
          />
        </Grid>

        {/* Sales by Status */}
        <Grid item xs={12} md={4}>
          <ComparisonChart
            title="Sales by Status"
            data={salesData?.sales_by_status || []}
            type="donut"
            dataKey="count"
            nameKey="status"
            height={350}
          />
        </Grid>

        {/* Top Performing Sales */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Top Performing Sales
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sale Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData?.top_sales?.slice(0, 10).map((sale) => (
                    <TableRow key={sale.id} hover>
                      <TableCell>{sale.name}</TableCell>
                      <TableCell>{sale.city}</TableCell>
                      <TableCell>
                        <Chip
                          label={sale.status}
                          size="small"
                          color={sale.status === 'completed' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {sale.start_date ? format(new Date(sale.start_date), 'MMM dd, yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ${sale.revenue.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Sales Performance Tab
  const SalesTab = () => {
    if (!salesData) return <CircularProgress />;

    return (
      <Grid container spacing={3}>
        {/* Sales by Month */}
        <Grid item xs={12} md={8}>
          <TrendChart
            title="Sales Volume by Month"
            data={salesData.sales_by_month}
            dataKeys={[
              { dataKey: 'count', name: 'Sales Count' },
              { dataKey: 'value', name: 'Estimated Value' }
            ]}
            xAxisKey="month_name"
            height={350}
          />
        </Grid>

        {/* Sales by Status */}
        <Grid item xs={12} md={4}>
          <ComparisonChart
            title="Sales Distribution"
            data={salesData.sales_by_status}
            type="pie"
            dataKey="count"
            nameKey="status"
            height={350}
          />
        </Grid>

        {/* Sales by Location */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Top Locations"
            data={salesData.sales_by_location}
            type="bar"
            dataKey="count"
            nameKey="city"
            height={400}
            horizontal
          />
        </Grid>

        {/* Location Value */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Location Total Value"
            data={salesData.sales_by_location}
            type="bar"
            dataKey="total_value"
            nameKey="city"
            height={400}
            yAxisFormat="currency"
            horizontal
          />
        </Grid>

        {/* Detailed Sales Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              All Sales Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sale Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>County</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData.top_sales.map((sale) => (
                    <TableRow key={sale.id} hover>
                      <TableCell>{sale.name}</TableCell>
                      <TableCell>{sale.city}</TableCell>
                      <TableCell>{sale.county || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={sale.status} size="small" />
                      </TableCell>
                      <TableCell>
                        {sale.start_date ? format(new Date(sale.start_date), 'MMM dd, yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell align="right">${sale.revenue.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Lead Analytics Tab
  const LeadsTab = () => {
    if (!leadsData) return <CircularProgress />;

    // Format funnel data
    const funnelData = leadsData.funnel.map(stage => ({
      stage: stage.stage.replace(/_/g, ' ').toUpperCase(),
      count: stage.count
    }));

    return (
      <Grid container spacing={3}>
        {/* Lead Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Leads"
            value={leadsData.funnel.reduce((sum, s) => sum + s.count, 0)}
            format="number"
            icon={<People />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Converted Leads"
            value={leadsData.funnel.find(s => s.stage === 'contracted')?.count || 0}
            format="number"
            icon={<TrendingUp />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Avg Conversion Time"
            value={leadsData.avg_conversion_time_days}
            format="number"
            icon={<CalendarToday />}
            color="info.main"
            subtitle="days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Lost Leads"
            value={leadsData.funnel.find(s => s.stage === 'lost')?.count || 0}
            format="number"
            icon={<Assessment />}
            color="error.main"
          />
        </Grid>

        {/* Conversion Funnel */}
        <Grid item xs={12} md={8}>
          <FunnelChart
            title="Lead Conversion Funnel"
            data={funnelData.filter(d => d.stage !== 'LOST')}
            nameKey="stage"
            valueKey="count"
            height={450}
          />
        </Grid>

        {/* Lead Sources */}
        <Grid item xs={12} md={4}>
          <ComparisonChart
            title="Lead Sources"
            data={leadsData.lead_sources}
            type="donut"
            dataKey="count"
            nameKey="source"
            height={450}
          />
        </Grid>

        {/* Leads by Month */}
        <Grid item xs={12}>
          <TrendChart
            title="New Leads Over Time"
            data={leadsData.leads_by_month}
            dataKeys={[{ dataKey: 'count', name: 'New Leads' }]}
            xAxisKey="month_name"
            height={300}
            type="area"
          />
        </Grid>
      </Grid>
    );
  };

  // Inventory Analytics Tab
  const InventoryTab = () => {
    if (!inventoryData) return <CircularProgress />;

    return (
      <Grid container spacing={3}>
        {/* Inventory Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Items"
            value={inventoryData.total_items}
            format="number"
            icon={<Inventory2 />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Items Sold"
            value={inventoryData.sold_items}
            format="number"
            icon={<ShoppingCart />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Turnover Rate"
            value={inventoryData.turnover_rate}
            format="percent"
            icon={<TrendingUp />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Avg Time to Sell"
            value={inventoryData.avg_time_to_sell_days}
            format="number"
            icon={<CalendarToday />}
            color="warning.main"
            subtitle="days"
          />
        </Grid>

        {/* Items by Category */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Items by Category"
            data={inventoryData.items_by_category}
            type="bar"
            dataKey="count"
            nameKey="category"
            height={400}
          />
        </Grid>

        {/* Category Revenue */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Revenue by Category"
            data={inventoryData.items_by_category.filter(c => c.sold_value > 0)}
            type="bar"
            dataKey="sold_value"
            nameKey="category"
            height={400}
            yAxisFormat="currency"
          />
        </Grid>

        {/* Price Distribution */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Price Distribution"
            data={inventoryData.price_distribution}
            type="bar"
            dataKey="count"
            nameKey="range"
            height={350}
          />
        </Grid>

        {/* Slow-Moving Items */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Slow-Moving Items Alert
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Items available for 30+ days
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryData.slow_moving_items.slice(0, 8).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">${item.price.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${item.days_available}d`}
                          size="small"
                          color="warning"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Revenue Tab
  const RevenueTab = () => {
    if (!revenueData) return <CircularProgress />;

    return (
      <Grid container spacing={3}>
        {/* Monthly Revenue Trend */}
        <Grid item xs={12}>
          <TrendChart
            title="Monthly Revenue vs Target"
            data={revenueData.monthly_revenue}
            dataKeys={[
              { dataKey: 'actual', name: 'Actual Revenue' },
              { dataKey: 'target', name: 'Target Revenue' }
            ]}
            xAxisKey="month_name"
            height={350}
            type="area"
            yAxisFormat="currency"
          />
        </Grid>

        {/* Revenue by Category */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Revenue by Category"
            data={revenueData.revenue_by_category}
            type="donut"
            dataKey="revenue"
            nameKey="category"
            height={400}
            yAxisFormat="currency"
          />
        </Grid>

        {/* Revenue by Payment Method */}
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Revenue by Payment Method"
            data={revenueData.revenue_by_method}
            type="bar"
            dataKey="revenue"
            nameKey="method"
            height={400}
            yAxisFormat="currency"
          />
        </Grid>

        {/* Revenue by Sale */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Revenue by Sale
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sale Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revenueData.revenue_by_sale.map((sale) => (
                    <TableRow key={sale.sale_id} hover>
                      <TableCell>{sale.sale_name}</TableCell>
                      <TableCell>
                        <Chip label={sale.status} size="small" />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ${sale.revenue.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Staff Performance Tab
  const StaffTab = () => {
    if (!staffData || !commissionsData) return <CircularProgress />;

    return (
      <Grid container spacing={3}>
        {/* Staff Leaderboard */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Sales Leaderboard
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell align="right">Sales</TableCell>
                    <TableCell align="right">Commission</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffData.sales_per_agent.map((agent, index) => (
                    <TableRow key={agent.employee_id}>
                      <TableCell>
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          color={index === 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{agent.name}</TableCell>
                      <TableCell align="right">{agent.sales_count}</TableCell>
                      <TableCell align="right">
                        ${agent.total_commission.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Hours Worked */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Hours Worked
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Staff Member</TableCell>
                    <TableCell align="right">Assignments</TableCell>
                    <TableCell align="right">Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffData.hours_worked.map((staff) => (
                    <TableRow key={staff.employee_id}>
                      <TableCell>{staff.name}</TableCell>
                      <TableCell align="right">{staff.assignments}</TableCell>
                      <TableCell align="right">
                        {staff.total_hours.toFixed(1)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Commission Charts */}
        <Grid item xs={12} md={8}>
          <TrendChart
            title="Commissions Over Time"
            data={commissionsData.by_month}
            dataKeys={[{ dataKey: 'amount', name: 'Commission Amount' }]}
            xAxisKey="month_name"
            height={350}
            type="area"
            yAxisFormat="currency"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <KPICard
                title="Total Paid"
                value={commissionsData.total_paid}
                format="currency"
                icon={<AttachMoney />}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12}>
              <KPICard
                title="Pending"
                value={commissionsData.total_pending}
                format="currency"
                icon={<Assessment />}
                color="warning.main"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Commission by Employee */}
        <Grid item xs={12}>
          <ComparisonChart
            title="Commission by Employee"
            data={commissionsData.by_employee}
            type="bar"
            dataKey={[
              { dataKey: 'paid', name: 'Paid' },
              { dataKey: 'pending', name: 'Pending' }
            ]}
            nameKey="name"
            height={400}
            stacked
            yAxisFormat="currency"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Analytics & Reports
        </Typography>
        <Box display="flex" gap={2}>
          <IconButton
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            color="primary"
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
          >
            <MenuItem onClick={handleExportCSV}>
              <Download sx={{ mr: 1 }} /> Export CSV
            </MenuItem>
            <MenuItem onClick={handlePrint}>
              <Print sx={{ mr: 1 }} /> Print Report
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Date Range Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <Typography variant="body1" fontWeight="medium">
            Date Range:
          </Typography>
          <ButtonGroup size="small">
            <Button
              variant={dateRange === 'thisMonth' ? 'contained' : 'outlined'}
              onClick={() => setDateRange('thisMonth')}
            >
              This Month
            </Button>
            <Button
              variant={dateRange === 'last3months' ? 'contained' : 'outlined'}
              onClick={() => setDateRange('last3months')}
            >
              Last 3 Months
            </Button>
            <Button
              variant={dateRange === 'last6months' ? 'contained' : 'outlined'}
              onClick={() => setDateRange('last6months')}
            >
              Last 6 Months
            </Button>
            <Button
              variant={dateRange === 'thisYear' ? 'contained' : 'outlined'}
              onClick={() => setDateRange('thisYear')}
            >
              This Year
            </Button>
            <Button
              variant={dateRange === 'custom' ? 'contained' : 'outlined'}
              onClick={() => setDateRange('custom')}
            >
              Custom
            </Button>
          </ButtonGroup>

          {dateRange === 'custom' && (
            <>
              <TextField
                label="Start Date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="End Date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" icon={<Assessment />} iconPosition="start" />
          <Tab label="Sales Performance" icon={<ShoppingCart />} iconPosition="start" />
          <Tab label="Lead Analytics" icon={<People />} iconPosition="start" />
          <Tab label="Inventory" icon={<Inventory2 />} iconPosition="start" />
          <Tab label="Revenue" icon={<AttachMoney />} iconPosition="start" />
          <Tab label="Staff & Commissions" icon={<TrendingUp />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {currentTab === 0 && <OverviewTab />}
            {currentTab === 1 && <SalesTab />}
            {currentTab === 2 && <LeadsTab />}
            {currentTab === 3 && <InventoryTab />}
            {currentTab === 4 && <RevenueTab />}
            {currentTab === 5 && <StaffTab />}
          </>
        )}
      </Box>
    </Container>
  );
};

export default Analytics;
